import {
  action,
  computed,
  configure,
  decorate,
  observable,
  reaction,
} from 'mobx';
import SimulcastLogger, {
  LOGGER_SETTINGS,
  SIM_LOG_KEY,
  // deleteSimLogFromLocalStorage,
} from 'shared/utils/SimulcastLogger';
import { formatDateTimeMS } from '../shared/utils/dateTime';
import { getRouteFromPathName } from '../shared/utils/localStorage';
import { toBoolean } from './utils';

configure({ enforceActions: 'always' });

const PREFIX = 'debug';

const random = (min, max) => Math.floor(Math.random() * (max - min)) + min;

export const storageKeys = {
  deleteOnBlockChange: 'deleteLogOnNextUnit',
  engineLogLevel: 'engLogLevel',
  maxLogStorageSizeInKb: 'maxLogSizeInKb',
  maxMessageLength: 'maxMessageLength',
  readLogWhenConnected: 'showLogOnSuccess',
  sendTimestampsToEngine: 'sendTimestampsToAuditLog',
  showViewer: 'showLogViewerScreens',
  // showViewer: 'showLogViewer',
  showViewerInFullscreen: 'logViewerFullscreen',
  skipPingPongWhenConnected: 'skipPingsOnSuccess',
  uiLogLevel: 'uiLogLevel',
  writeLogToConsole: 'showLogInConsole',
  writeLogToDevWindow: 'logToDevWindow',
  writeTimestampsToConsole: 'showTimestampsInConsole',
  writeToLogWhenConnected: 'loggedSuccessfulConnection',
};

class DebuggerStore {
  constructor(AuthStore, BidStore, AuctionStore) {
    this.authStore = AuthStore;
    this.auctionStore = AuctionStore;
    this.bidStore = BidStore;
    this.auctionId = this.auctionStore.auctionID;
    this.laneId = this.auctionStore.getAuctionLaneIdForInvocation;

    this.messageType = {
      ERROR: 'error',
      INFO: 'log',
      WARN: 'warn',
    };

    const { ERROR, INFO, WARN } = this.messageType;

    this.logLevels = {
      ERRORS: { key: 'errors', messageTypes: [ERROR] },
      OFF: { key: 'off', messageTypes: [] },
      VERBOSE: { key: 'verbose', messageTypes: [ERROR, INFO, WARN] },
    };

    this.logTargets = {
      ENGINE: 'engine',
      UI: 'ui',
    };

    this.pingPongEmits = ['ping', 'pong'];
    // this.initialize();
  }
  
  initialize = () => {
    console.log('initializing debugger store');
    // whether we should be logging anything at all (to do anything, this must be true);
    this.setDebugProperty('debugMode', true);
    this.setDebugProperty('showSettings', false);
    this.setDebugProperty('latestLogItem', null);
    this.setDebugProperty('filter', new Map());
    this.setDebugProperty('filterText', '');
    this.setDebugProperty('logView', 'collapsed');
    this.setDebugProperty('filteredData', []);
    this.setDebugProperty('logCount', 0);
    this.setDebugProperty('logData', []);

    this.setUpdated();

    if (this.getDebugItem(storageKeys.uiLogLevel) === null) {
      this.initializeLocalStorage();
    }

    // updated when some debug item is updated (so UI can observe changes)
    this.setUpdated();

    reaction(
      () => this.latestLogItem,
      (data) => {
        if (this.shouldWriteLogToConsole) {
          console.log(`Debugger > ${data.method} (${data?.slug})`, data);
        }
      },
    );
  };

  initializeLocalStorage = () => {
    const { ERRORS, OFF } = this.logLevels;
    const {
      deleteOnBlockChange,
      engineLogLevel,
      maxLogStorageSizeInKb,
      maxMessageLength,
      readLogWhenConnected,
      showViewer,
      showViewerInFullscreen,
      skipPingPongWhenConnected,
      uiLogLevel,
      writeLogToConsole,
      writeLogToDevWindow,
      writeToLogWhenConnected,
    } = storageKeys;
    console.log('**** initializeLocalStorage *****');

    localStorage.setItem('screen_id', random(1, 1000));

    // info we should pass to engine (requires debugMode to be true) (possible values: errors, verbose, null)
    this.setDebugItem(engineLogLevel, OFF.key);

    // info we should collect on ui (possible values: errors, verbose, null)
    this.setDebugItem(uiLogLevel, ERRORS.key);
    // this.setDebugItem(uiLogLevel, VERBOSE.key);

    // whether we should log the send/receive messages and their timestamps
    this.setLogTimestamp(false, false);

    // whether we delete logs from storage upon block change
    this.setDebugItem(deleteOnBlockChange, true);

    // whether to write anything to the dev window
    this.setDebugItem(writeLogToDevWindow, true);

    // allow for viewing log when we have a successful connection
    this.setDebugItem(readLogWhenConnected, true);

    // enable when we don't want to see all the ping/pongs
    this.setDebugItem(skipPingPongWhenConnected, false);

    // write to log when we successfully connect
    this.setDebugItem(writeToLogWhenConnected, false);

    // write logs to console log
    this.setDebugItem(writeLogToConsole, false);

    // whether to show the logs on screen
    // this.setDebugItem(showViewer, this.isDebugMode);
    this.setDebugItem(showViewer, false);

    // whether to show the logs in fullscreen mode
    this.setDebugItem(showViewerInFullscreen, false);

    // size in KB of the log size (max size of local storage is 5MB, so I would not set this value to be over 4000KB)
    this.setDebugItem(maxLogStorageSizeInKb, 1000);

    // the number of characters in the message to log (NOTE: some payloads are very large and we don't need to see all of it)
    this.setDebugItem(maxMessageLength, 150);
  };

  getHandle = () => localStorage.getItem('screen_id');
  // getDebugModeValue = () => {
  //   const debugging = this.bidStore.auction.debugging;

  //   console.log('getDebugModeValue', debugging);
  //   if (!debugging) return false;

  //   if (debugging === 'DebuggingErrorsOff') return false;
  //   if (debugging === 'DebuggingErrorsOn') return true;
  // };

  setLogTimestamp = (enable, showConsole) => {
    const { sendTimestampsToEngine, writeTimestampsToConsole } = storageKeys;

    // whether we should log the send/receive messages and their timestamps
    this.setDebugItem(sendTimestampsToEngine, enable);

    if (enable) {
      // whether to console.log out the event names and timestamps
      this.setDebugItem(writeTimestampsToConsole, showConsole);
    } else {
      // remove this from local storage if exists
      this.removeDebugItem(writeTimestampsToConsole);
    }
  };

  setUpdated = () => {
    this.setDebugProperty('debuggerUpdated', formatDateTimeMS());
  };

  clearDebugItems = () => {
    const keys = Object.keys(localStorage);
    for (let key of keys) {
      if (key.startsWith(PREFIX)) {
        localStorage.removeItem(key);
      }
    }
    this.setUpdated();
  };

  removeDebugItem = (key) => {
    const updatedKey = this.getKey(key);
    const item = localStorage.getItem(updatedKey);
    if (item) {
      localStorage.removeItem(updatedKey);
      this.setUpdated();
    }
  };

  getKey = (key) => `${PREFIX}${this.getHandle() ?? ''}_${key}`;

  setDebugItem = (key, value, autoUpdate = true) => {
    const updatedKey = this.getKey(key);
    //console.log(key, value, { isDebugMode: this.isDebugMode})
    if (!this.isDebugMode) {
      localStorage.removeItem(updatedKey);
      return;
    }

    localStorage.setItem(updatedKey, JSON.stringify(value));
    if (autoUpdate) this.setUpdated();
  };

  getDebugItem = (key) => {
    const value = localStorage.getItem(this.getKey(key));
    return value && value !== null ? JSON.parse(value) : value;
  };

  removeDebugArrayItem = (key, value) => {
    const arr = this.getDebugArrayValue(key);
    const filtered = arr.filter((item) => item !== value);
    this.setDebugItem(key, filtered);
  };

  setDebugArrayItem = (key, value) => {
    const arr = this.getDebugArrayValue(key);
    this.setDebugItem(key, [...arr, value]);
  };

  getDebugArrayValue = (key) => {
    const ls = this.getDebugItem(key);
    return ls ? ls : [];
  };

  getDebugBooleanValue = (key) => toBoolean(this.getDebugItem(key));

  getDebugNumberValue = (key) => Number(this.getDebugItem(key));

  getDebugJsonValue = (key) => {
    const value = this.getDebugJsonValue(key);
    return value && value !== null ? JSON.parse(value) : null;
  };

  setDebugProperty = (key, value) => (this[key] = value);
  getDebugProperty = (key) => this[key];

  updateFilterText = (key, value) => this.filter.set(key, value);

  updateFilter2 = (key, value) => {
    if (this.filter.get(key) === value) {
      this.filter.delete(key);
    } else {
      this.filter.set(key, value);
    }
    this.setUpdated();
  };

  getFilter = (key) => this.filter.get(key);

  log = ({
    component = null,
    logLevel = this.uiLogLevel,
    message = null,
    messageType = this.messageType.ERROR,
    method = '',
    params = {},
    settings = LOGGER_SETTINGS,
    user = this.bidStore.user,
  }) => {
    if (this.isDebugMode) {
      const mergedParams = {
        ...{
          auctionId: this.auctionId,
          ipAddress: this.authStore.settings?.ipAddress,
          laneId: this.laneId,
          storageParams: {
            block: this.bidStore.auction,
          },
        },
        ...params,
      };

      const logger = new SimulcastLogger({
        component,
        logLevel,
        message,
        messageType,
        method,
        params: mergedParams,
        settings,
        title: '',
        user,
        onUpdated: (value) => {
          this.setDebugProperty('latestLogItem', value);
          this.setUpdated();
        },
      });

      logger.logToStorage(this.simLogTooLarge());

      const simLogData = localStorage.getItem(SIM_LOG_KEY);
      const logData = simLogData ? JSON.parse(simLogData) : [];
      this.setDebugProperty('logData', logData);
      this.setDebugProperty('logCount', logData.length);
      this.setUpdated();
    }
  };

  getLog = ({ target = null } = {}) => {
    if (!target) return this.logData;

    const { ENGINE, UI } = this.logTargets;
    let logLevel = null;
    if (target === UI) {
      logLevel = this.uiLogLevel;
    } else if (target === ENGINE) {
      logLevel = this.engineLogLevel;
    }

    const filter = this.filter;
    if (logLevel) {
      return this.logData
        .filter(
          (data) =>
            this.logLevels[logLevel?.toUpperCase()].messageTypes ||
            [].includes(data?.messageType),
        )
        .filter(({ slug }) =>
          !filter.get('screen')
            ? true
            : slug && slug.toLowerCase() === filter.get('screen'),
        )
        .filter(({ messageType }) =>
          !filter.get('messageType')
            ? true
            : messageType && messageType === filter.get('messageType'),
        )
        .filter(({ message }) =>
          !filter.get('messageText')
            ? true
            : message &&
              message
                ?.toLowerCase()
                .indexOf(filter.get('messageText').toLowerCase()) > -1,
        )
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    } else {
      return [];
    }
  };

  deleteLog = () => {
    // console.log('deleteLog');
    //deleteSimLogFromLocalStorage();

    localStorage.removeItem('simulcast_log');

    localStorage.removeItem('simulcast_ls_size_KB');

    if (this.shouldWriteLogToConsole) {
      console.log(
        `%c Debugger > =========== CHANGED BLOCK ITEM (DELETED LOG) ===========`,
        'background: #f00; font-weight: bold; color: #fff',
      );
    }

    this.setDebugProperty('logData', []);
    this.setDebugProperty('logCount', 0);
    this.setUpdated();
  };

  checkPingPong = (method) => {
    const isPingPong = this.pingPongEmits.includes(method.toLowerCase());
    const shouldSkipAndIsPingPong =
      this.shouldSkipPingPongWhenConnected && isPingPong;
    const shouldIgnorePingPongSkipCheck = !this.shouldSkipPingPongWhenConnected;
    const executeBlock =
      !shouldSkipAndIsPingPong || shouldIgnorePingPongSkipCheck;
    // console.log('checkPingPong', {executeBlock, isPingPong, method})
    return executeBlock;
  };

  get isDebugMode() {
    return (
      this.debuggerUpdated && toBoolean(this.getDebugProperty('debugMode'))
    );
  }

  get shouldDeleteOnBlockChange() {
    return (
      this.debuggerUpdated &&
      this.getDebugBooleanValue(storageKeys.deleteOnBlockChange)
    );
  }

  get engineLogLevel() {
    return (
      this.debuggerUpdated && this.getDebugItem(storageKeys.engineLogLevel)
    );
  }

  get uiLogLevel() {
    return this.debuggerUpdated && this.getDebugItem(storageKeys.uiLogLevel);
  }

  get maxLogStorageSize() {
    return (
      this.debuggerUpdated &&
      this.getDebugNumberValue(storageKeys.maxLogStorageSizeInKb)
    );
  }

  get maxMessageLength() {
    return (
      this.debuggerUpdated &&
      this.getDebugNumberValue(storageKeys.maxMessageLength)
    );
  }

  get shouldSendTimestampsToEngine() {
    return (
      this.debuggerUpdated &&
      this.getDebugBooleanValue(storageKeys.sendTimestampsToEngine)
    );
  }

  get shouldWriteTimstampsToConsole() {
    return (
      this.debuggerUpdated &&
      this.getDebugBooleanValue(storageKeys.writeTimestampsToConsole)
    );
  }

  get shouldShowLogViewer() {
    const slug = getRouteFromPathName()?.toLowerCase();
    const exists = this.getDebugArrayValue(storageKeys.showViewer).includes(
      slug,
    );

    // FIXME: FOR THE LOGIN PAGE, WE DON'T INITIALIZE THIS STORE, SO WE WILL HAVE A LS KEY WITHOUT A HANDLE. AS A RESULT, WE MAY HAVE 2 KEYS (IF WE HAVE 2 THEN DELETE THE ONE WITHOUT THE HANDLE)

    return (
      this.debuggerUpdated && exists //this.getDebugBooleanValue(storageKeys.showViewer)
    );
  }

  get shouldShowLogViewerInFullscreen() {
    const slug = getRouteFromPathName()?.toLowerCase();
    const exists = this.getDebugArrayValue(
      storageKeys.showViewerInFullscreen,
    ).includes(slug);

    // FIXME: FOR THE LOGIN PAGE, WE DON'T INITIALIZE THIS STORE, SO WE WILL HAVE A LS KEY WITHOUT A HANDLE. AS A RESULT, WE MAY HAVE 2 KEYS (IF WE HAVE 2 THEN DELETE THE ONE WITHOUT THE HANDLE)
    return (
      this.debuggerUpdated && exists //this.getDebugBooleanValue(storageKeys.showViewerInFullscreen)
    );
  }

  get shouldSkipPingPongWhenConnected() {
    return (
      this.debuggerUpdated &&
      this.getDebugBooleanValue(storageKeys.skipPingPongWhenConnected)
    );
  }

  get shouldReadLogWhenConnected() {
    return (
      this.debuggerUpdated &&
      this.getDebugBooleanValue(storageKeys.readLogWhenConnected)
    );
  }

  get shouldWriteLogWhenConnected() {
    return (
      this.debuggerUpdated &&
      this.getDebugBooleanValue(storageKeys.writeToLogWhenConnected)
    );
  }

  get shouldWriteLogToConsole() {
    return (
      this.debuggerUpdated &&
      this.getDebugBooleanValue(storageKeys.writeLogToConsole)
    );
  }

  get shouldWriteLogToDevWindow() {
    return (
      this.debuggerUpdated &&
      this.getDebugBooleanValue(storageKeys.writeLogToDevWindow)
    );
  }

  get errorsExistInLog() {
    return (
      (this.debuggerUpdated &&
        this.logData?.filter((items) => items.messageType === 'error')
          .length) ||
      false
    );
  }

  getSizeInKb = (value) => Number(value / 1024);

  getSimLogSize = () => {
    if (!localStorage.getItem(SIM_LOG_KEY)) return 0;
    const size =
      (localStorage.getItem(SIM_LOG_KEY)?.length + SIM_LOG_KEY.length) * 2;
    return this.getSizeInKb(size);
  };

  simLogTooLarge = () => {
    return this.getSimLogSize() > this.maxLogStorageSize;
  };

  handleDefaultSettings = () => {
    this.setDebugItem(storageKeys.writeLogToDevWindow, true, false);
    this.setDebugItem(storageKeys.readLogWhenConnected, true, false);
    this.setDebugItem(storageKeys.writeToLogWhenConnected, false, false);
    this.setDebugItem(storageKeys.writeLogToConsole, false, false);
    this.setDebugItem(storageKeys.sendTimestampsToEngine, false, false);
    this.setDebugItem(storageKeys.writeTimestampsToConsole, false, false);
    this.setDebugItem(storageKeys.deleteOnBlockChange, true, false);
    this.setDebugItem(storageKeys.skipPingPongWhenConnected, false, false);
    this.setDebugItem(storageKeys.maxLogStorageSizeInKb, 1000, false);
    this.setDebugItem(storageKeys.maxMessageLength, 150, false);
    this.setDebugItem(storageKeys.engineLogLevel, 'off', false);
    this.setDebugItem(storageKeys.uiLogLevel, 'errors', false);
    this.setUpdated();
  };

  handleDisableLogging = () => {
    this.setDebugItem(storageKeys.writeLogToDevWindow, false, false);
    this.setDebugItem(storageKeys.readLogWhenConnected, true, false);
    this.setDebugItem(storageKeys.writeToLogWhenConnected, false, false);
    this.setDebugItem(storageKeys.writeLogToConsole, false, false);
    this.setDebugItem(storageKeys.sendTimestampsToEngine, false, false);
    this.setDebugItem(storageKeys.writeTimestampsToConsole, false, false);
    this.setDebugItem(storageKeys.deleteOnBlockChange, false, false);
    this.setDebugItem(storageKeys.skipPingPongWhenConnected, true, false);
    this.setDebugItem(storageKeys.maxLogStorageSizeInKb, 1000, false);
    this.setDebugItem(storageKeys.maxMessageLength, 150, false);
    this.setDebugItem(storageKeys.engineLogLevel, 'off', false);
    this.setDebugItem(storageKeys.uiLogLevel, 'off', false);
    this.setUpdated();
    this.deleteLog();
  };
}

decorate(DebuggerStore, {
  checkPingPong: action,
  clearStorageItems: action,
  debugMode: observable,
  debuggerUpdated: observable,
  deleteLog: action,
  engineLogLevel: computed,
  errorsExistInLog: computed,
  filter: observable,
  filterText: observable,
  filteredData: observable,
  getDebugArrayValue: action,
  getDebugBooleanValue: action,
  getDebugItem: action,
  getDebugJsonValue: action,
  getDebugModeValue: action,
  getDebugNumberValue: action,
  getDebugProperty: action,
  getFilter: action,
  getHandle: action,
  getLog: action,
  getSimLogSize: action,
  getSizeInKb: action,
  handleDefaultSettings: action,
  handleDisableLogging: action,
  initialize: action,
  initializeLocalStorage: action,
  isDebugMode: computed,
  latestLogItem: observable,
  log: action,
  logCount: observable,
  logData: observable,
  logView: observable,
  maxLogStorageSize: computed,
  maxMessageLength: computed,
  removeDebugArrayItem: action,
  removeDebugItem: action,
  setDebugArrayItem: action,
  setDebugItem: action,
  setDebugProperty: action,
  setUpdated: action,
  shouldDeleteOnBlockChange: computed,
  shouldReadLogWhenConnected: computed,
  shouldSendTimestampsToEngine: computed,
  shouldShowLogViewer: computed,
  shouldShowLogViewerInFullscreen: computed,
  shouldSkipPingPongWhenConnected: computed,
  shouldWriteLogToConsole: computed,
  shouldWriteLogToDevWindow: computed,
  shouldWriteLogWhenConnected: computed,
  shouldWriteTimstampsToConsole: computed,
  showSettings: observable,
  simLogTooLarge: action,
  uiLogLevel: computed,
  updateFilter2: action,
  updateFilterText: action,
});

export default DebuggerStore;
